import { useEffect, useState } from "react";
import { startIeltsExam, submitExamPart, finishExam, type ExamSnapshot, type ExamPart } from "../lib/api";

interface Props { onClose: () => void; }

const PART_LABEL: Record<string, string> = {
  part1: "Part 1 — Introduction (~4-5 min)",
  part2: "Part 2 — Cue card (~3-4 min total)",
  part3: "Part 3 — Discussion (~4-5 min)",
};

function bandColor(b: number): string {
  if (b >= 7) return "text-emerald-300";
  if (b >= 6) return "text-lime-300";
  if (b >= 5) return "text-amber-300";
  return "text-rose-300";
}

export function ExamView({ onClose }: Props): JSX.Element {
  const [exam, setExam] = useState<ExamSnapshot | null>(null);
  const [partIdx, setPartIdx] = useState(0);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const e = await startIeltsExam();
      if (e) setExam(e);
      else setError("Could not start exam.");
    })();
  }, []);

  if (error) return (
    <div className="rounded-2xl border border-rose-700/40 bg-rose-500/10 p-6 text-rose-200 text-sm">{error}</div>
  );
  if (!exam) return (
    <div className="rounded-2xl border border-ink-700 bg-ink-900/60 p-8 text-center text-sm text-ink-400">Preparing mock test…</div>
  );

  if (exam.status === "completed") {
    return (
      <div className="space-y-4">
        <div className="rounded-2xl border border-emerald-700/40 bg-emerald-500/10 p-6 text-center">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300/80">IELTS Speaking estimate</div>
          <div className={`text-5xl font-bold mt-2 ${bandColor(exam.overall_band ?? 0)}`}>
            {(exam.overall_band ?? 0).toFixed(1)}
          </div>
          <div className="text-xs text-ink-400 mt-1">overall band</div>
          {exam.feedback && (
            <p className="text-sm text-ink-200 mt-3 text-left whitespace-pre-line">{exam.feedback}</p>
          )}
        </div>
        {exam.parts.map((p) => (
          <div key={p.name} className="rounded-2xl border border-ink-700 bg-ink-900/60 p-4">
            <div className="text-[10px] uppercase tracking-widest text-ink-400">{PART_LABEL[p.name]}</div>
            {p.bands && (
              <div className="mt-2 grid grid-cols-4 gap-2 text-xs">
                <Band label="F&C" v={p.bands.fluency_coherence} />
                <Band label="LR" v={p.bands.lexical_resource} />
                <Band label="GR" v={p.bands.grammatical_range} />
                <Band label="Pron" v={p.bands.pronunciation} />
              </div>
            )}
            {p.feedback && <div className="text-xs text-ink-300 mt-2">{p.feedback}</div>}
          </div>
        ))}
        <button onClick={onClose} className="w-full rounded-xl bg-lime-500 hover:bg-lime-400 text-ink-900 font-semibold py-3">
          Back to dashboard
        </button>
      </div>
    );
  }

  const current = exam.parts[partIdx];
  const isLast = partIdx === exam.parts.length - 1;

  async function next(): Promise<void> {
    if (!draft.trim()) {
      setError("Please write a response first.");
      return;
    }
    setBusy(true);
    setError(null);
    const updated = await submitExamPart(exam!.id, current.name, draft);
    if (!updated) {
      setError("Submission failed.");
      setBusy(false);
      return;
    }
    setExam(updated);
    setDraft("");
    if (isLast) {
      const finished = await finishExam(exam!.id);
      if (finished) setExam(finished);
    } else {
      setPartIdx(partIdx + 1);
    }
    setBusy(false);
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-sky-700/40 bg-sky-500/5 p-5">
        <div className="text-[10px] uppercase tracking-widest text-sky-300/80">{PART_LABEL[current.name]}</div>
        <h2 className="text-lg font-semibold text-white mt-1 whitespace-pre-line">{current.prompt}</h2>
        {current.hint && <p className="text-xs text-ink-300 mt-2">{current.hint}</p>}
      </div>

      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={8}
        className="w-full rounded-xl bg-ink-800 border border-ink-700 px-4 py-3 text-sm text-white placeholder-ink-500 focus:outline-none focus:border-sky-500"
        placeholder="Speak (your phone keyboard mic icon works) or type your answer here…"
      />

      {error && <div className="text-rose-300 text-xs">{error}</div>}

      <div className="flex items-center justify-between">
        <button onClick={onClose} className="text-xs text-ink-400 hover:text-white">← Cancel exam</button>
        <button
          onClick={next}
          disabled={busy}
          className="rounded-xl bg-sky-500 hover:bg-sky-400 text-ink-900 font-semibold px-5 py-2.5 disabled:opacity-40"
        >
          {busy ? "Grading…" : isLast ? "Finish exam →" : "Submit & next part →"}
        </button>
      </div>

      <div className="flex items-center gap-2 mt-2">
        {exam.parts.map((p, i) => (
          <div
            key={p.name}
            className={`h-1 flex-1 rounded-full transition ${i < partIdx ? "bg-emerald-400" : i === partIdx ? "bg-sky-400" : "bg-ink-700"}`}
          />
        ))}
      </div>
    </div>
  );
}

function Band({ label, v }: { label: string; v: number }): JSX.Element {
  return (
    <div className="rounded-lg border border-ink-700 bg-ink-800/60 px-2 py-1.5 text-center">
      <div className="text-[10px] uppercase tracking-widest text-ink-500">{label}</div>
      <div className="font-bold text-white">{v.toFixed(1)}</div>
    </div>
  );
}
